import { forwardRef, useId, type ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@eon/utils';
import { useFieldA11yProps } from './field';

/**
 * SelectField is the project-wide replacement for native <select>. It
 * renders a Radix listbox so styling, keyboard nav and a11y match the rest
 * of the UI.
 *
 * Two usage modes:
 *
 *   1) Declarative — pass an `options` array. Easiest:
 *
 *        <SelectField
 *          value={kind}
 *          onValueChange={setKind}
 *          options={[{ value: 'Soft', label: 'Soft' }, { value: 'Hard', label: 'Hard' }]}
 *        />
 *
 *   2) Children — pass <SelectFieldOption>s for grouping or richer items.
 *
 *        <SelectField value={x} onValueChange={setX}>
 *          <SelectFieldOption value="a">A</SelectFieldOption>
 *          <SelectFieldOption value="b">B</SelectFieldOption>
 *        </SelectField>
 */

export interface SelectOption<T extends string = string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface SelectFieldProps<T extends string = string> {
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  options?: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  size?: 'sm' | 'md';
  className?: string;
  children?: ReactNode;
  /** Override the rendered button id (otherwise auto). Usually unused. */
  id?: string;
}

export function SelectField<T extends string = string>({
  value,
  defaultValue,
  onValueChange,
  options,
  placeholder = 'Seçiniz…',
  disabled,
  required,
  name,
  size = 'md',
  className,
  children,
  id: idProp,
}: SelectFieldProps<T>) {
  const fieldA11y = useFieldA11yProps();
  const auto = useId();
  const id = idProp ?? (fieldA11y.id as string | undefined) ?? auto;
  const sizeCls = size === 'sm' ? 'h-9 text-[13px]' : 'h-10 text-sm';
  return (
    <SelectPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange as (v: string) => void}
      disabled={disabled}
      required={required}
      name={name}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={fieldA11y['aria-invalid']}
        aria-describedby={fieldA11y['aria-describedby']}
        className={cn(
          'border-input bg-background ring-offset-background flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2',
          'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
          sizeCls,
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'bg-popover text-popover-foreground z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border shadow-md',
          )}
        >
          <SelectPrimitive.Viewport className="max-h-72 p-1">
            {options
              ? options.map((opt) => (
                  <SelectFieldOption
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                    description={opt.description}
                  >
                    {opt.label}
                  </SelectFieldOption>
                ))
              : children}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export interface SelectFieldOptionProps {
  value: string;
  disabled?: boolean;
  description?: ReactNode;
  children: ReactNode;
}

export const SelectFieldOption = forwardRef<HTMLDivElement, SelectFieldOptionProps>(
  ({ value, disabled, description, children }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        'relative flex cursor-default select-none items-start gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
      )}
    >
      <span className="absolute left-2 top-2 flex h-4 w-4 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <span className="flex flex-col">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        {description ? (
          <span className="text-muted-foreground text-[11px]">{description}</span>
        ) : null}
      </span>
    </SelectPrimitive.Item>
  ),
);
SelectFieldOption.displayName = 'SelectFieldOption';
